import Papa from 'papaparse';
import { Alert, Badge, Box, Button, Flex, GridItem, Heading, Link, SystemStyleObject, Text } from '@chakra-ui/react';
import { ArrowLeftIcon, CalendarIcon, ChatIcon, TimeIcon, VideoIcon } from '@udacity/chakra-uds-icons';
import { DataTable } from 'app/common/components/uds-table';
import { CheckboxConfig, DataTableColumn, Filter, PaginationData } from 'app/common/components/uds-table/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useGetMeetingUsersQuery } from '../queries/enhanced';
import { commonLanguages } from 'app/helpers/form-helper';
import { MeetingAttendanceStatus, MeetingUser } from 'app/types/uconnect';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { dateFormatter, durationInMinutes } from 'app/helpers/format-helper';
import { AttendanceCell } from './attendance-cell';
import { AttendanceProvenanceBadge } from './attendance-provenance-badge';
import { AttendanceSelectionControls } from './attendance-selection-controls';
import { BulkAttendanceButtons } from './bulk-attendance-buttons';
import { LearnerNameCell } from './learner-name-cell';
import { UnmatchedZoomParticipants } from './unmatched-zoom-participants';
import { MatchedZoomParticipant, useZoomAttendanceSync } from './use-zoom-attendance-sync';
import { downloadCsvString, getZoomSyncErrorMessage, isPastMeeting } from '../helpers/utils';
import {
  getQueryParams,
  standardOnSelect,
  standardOnSelectAll,
  standardOnSelectMany,
} from 'app/common/components/uds-table/utils';

const meetingUserPaginationData: PaginationData = {
  defaultPageSize: 100,
};

// Number of learners fetched before we know the roster size. If a meeting has
// more attendees, we refetch with the full count (see effect below).
const INITIAL_FETCH_LIMIT = 200;

const ATTENDANCE_STATUS_FILTER_ID = 'attendanceStatus';
// Sentinel filter value for learners that have no recorded attendance.
const NOT_RECORDED_FILTER_VALUE = 'NOT_RECORDED';

function matchesAttendanceStatus(
  learner: { attendance?: { status?: MeetingAttendanceStatus | null } | null },
  statusFilter: string | undefined
): boolean {
  if (!statusFilter) return true;
  if (statusFilter === NOT_RECORDED_FILTER_VALUE) return !learner.attendance?.status;
  return learner.attendance?.status === statusFilter;
}

const joinButtonStyles: SystemStyleObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  flexShrink: 0,
  px: 6,
  h: 10,
  borderRadius: 'base',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 'wider',
  bg: 'white',
  color: 'blue.800',
  zIndex: 1,
  textDecoration: 'none',
  _hover: { bg: 'gray.100', textDecoration: 'none' },
};

const ExternalLink = Link as React.FC<{
  href: string;
  isExternal?: boolean;
  sx?: SystemStyleObject;
  children?: React.ReactNode;
}>;

const JoinMeetingButton: React.FC<{ href: string; label: string }> = ({ href, label }) => (
  <ExternalLink href={href} isExternal sx={joinButtonStyles}>
    <VideoIcon color="blue.500" h={5} w={5} />
    {label}
  </ExternalLink>
);

export const MeetingOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useAppToast();
  const [selectedUserKeys, setSelectedUserKeys] = useState<string[]>([]);
  const [fetchLimit, setFetchLimit] = useState(INITIAL_FETCH_LIMIT);

  const params = useParams<{ meetingId: string }>();
  const { meetingId } = params;

  const { data, isLoading, isFetching } = useGetMeetingUsersQuery(
    { meetingId, offset: 0, limit: fetchLimit },
    { skip: !meetingId }
  );

  const meeting = data?.meetings?.meetings[0];
  const totalCount = meeting?.meetingUsers.totalCount;

  // Once we know the roster is larger than the initial fetch, grab everyone so
  // sorting/filtering/select-all operate on the full set.
  useEffect(() => {
    if (totalCount && totalCount > fetchLimit) {
      setFetchLimit(totalCount);
    }
  }, [totalCount, fetchLimit]);

  // Zoom only has attendance to report once the meeting is over.
  const canSyncFromZoom = !!meeting?.conferenceLink && isPastMeeting(meeting);
  const zoomSync = useZoomAttendanceSync(meetingId, canSyncFromZoom);

  const handleZoomSync = async () => {
    const result = await zoomSync.sync();
    if (result) {
      toast({ variant: 'success', description: t('connect.zoomSync.synced', { updated: result.updatedCount }) });
    }
  };

  const date = dateFormatter.short(meeting?.startAt);
  const startTime = dateFormatter.localTime(meeting?.startAt);
  const endTime = dateFormatter.localTime(meeting?.endAt);

  const languages =
    meeting?.locales.map(
      (locale) =>
        commonLanguages.find((lang) => locale === lang.code) || {
          name: 'English',
        }
    ) || [];

  const allStudents = meeting?.meetingUsers?.meetingUsers || [];

  const statusFilter = getQueryParams()[ATTENDANCE_STATUS_FILTER_ID];
  const students = allStudents.filter((learner) => matchesAttendanceStatus(learner, statusFilter));

  // Reset the selection when the active filter changes so bulk actions only
  // ever apply to the learners currently shown.
  useEffect(() => {
    setSelectedUserKeys([]);
  }, [statusFilter]);

  // Keys for every learner matching the active filter — not just the current
  // page. The "Select All" button operates on this full set so facilitators can
  // bulk-update the entire (filtered) roster at once, even when it spans many
  // pages (e.g. every "Not recorded" learner).
  const filteredUserKeys = students.map((student) => student.userKey);
  const selectedKeySet = new Set(selectedUserKeys);
  const allFilteredSelected = filteredUserKeys.length > 0 && filteredUserKeys.every((key) => selectedKeySet.has(key));

  // "Select All" button: select the whole (filtered) roster, or clear it.
  const onSelectAllFiltered = standardOnSelectAll(filteredUserKeys, selectedUserKeys, setSelectedUserKeys);

  // Header checkbox: scope to just the rows on the current page. Selecting adds
  // the visible learners to the selection; deselecting removes only those,
  // leaving any learners selected on other pages untouched.
  const onSelectVisible = standardOnSelectMany(selectedUserKeys, setSelectedUserKeys);

  const checkboxConfig: CheckboxConfig = {
    checkboxId: 'userKey',
    onSelectRow: standardOnSelect(selectedUserKeys, setSelectedUserKeys),
    onSelectAll: (shouldSelect, visibleIds) => onSelectVisible(shouldSelect, visibleIds.map(String)),
    selectedIds: selectedUserKeys,
  };

  const attendanceStatusFilter: Filter = {
    id: ATTENDANCE_STATUS_FILTER_ID,
    label: t('connect.filterByAttendance'),
    minWidth: '260px',
    options: [
      { label: t('connect.attended'), value: MeetingAttendanceStatus.Attended },
      { label: t('connect.absent'), value: MeetingAttendanceStatus.Absent },
      { label: t('connect.excused'), value: MeetingAttendanceStatus.Excused },
      { label: t('connect.notRecorded'), value: NOT_RECORDED_FILTER_VALUE },
    ],
  };

  const handleCsvDownload = () => {
    const rows = allStudents.map((student) => {
      const zoomMatch = zoomSync.matchedByUserKey[student.userKey];
      return {
        studentID: student.userKey,
        email: student.user?.email,
        firstName: student.user?.firstName,
        lastName: student.user?.lastName,
        seriesName: meeting?.series?.name,
        seriesId: meeting?.series?.id,
        attendance: student.attendance?.status,
        zoomJoinTime: zoomMatch?.joinTime,
        zoomDurationMinutes: zoomMatch ? durationInMinutes(zoomMatch.durationSeconds) : undefined,
      };
    });
    downloadCsvString(Papa.unparse(rows), `${meeting?.name || 'meeting'}-students.csv`);
  };

  return (
    <GridItem gridColumn="1 / -1">
      <Button as={RouterLink} to="/connect" variant="outline" mb={12} leftIcon={<ArrowLeftIcon h={6} w={6} />}>
        {t('common.back')}
      </Button>
      <Box borderRadius="base" border="1px solid" borderColor="gray.300" bg="white">
        <Flex
          borderTopRadius="base"
          bg="blue.800"
          color="white"
          p={10}
          gap={6}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Flex flexDir="column" gap={4}>
            <Heading size="h2" as="h1">
              {meeting?.name || `${date} ${startTime} - ${endTime}`}
            </Heading>
            <Text fontWeight={600}>{meeting?.series?.name}</Text>
            <Flex gap={2}>
              <Badge bg="white" color="blue.800">
                <CalendarIcon h={4} mb={0.5} w={4} /> {date}
              </Badge>
              <Badge bg="white" color="blue.800">
                <TimeIcon h={4} mb={0.5} w={4} /> {startTime} - {endTime}
              </Badge>
              {languages.map((language) => (
                <Badge bg="white" color="blue.800" key={language.name}>
                  <ChatIcon h={4} mb={0.5} w={4} /> {language.name}
                </Badge>
              ))}
            </Flex>
            {meeting?.instructions && <Text whiteSpace="pre-line">{meeting.instructions}</Text>}
          </Flex>
          {meeting?.conferenceLink && <JoinMeetingButton href={meeting.conferenceLink} label={t('connect.join')} />}
        </Flex>
        <Box p={10}>
          <Flex justifyContent="space-between">
            <Heading size="h3" as="h2" mb={8}>
              {t('connect.attendance')}
            </Heading>
            <Flex gap={6} alignItems="center">
              {canSyncFromZoom && (
                <Button variant="outline" onClick={handleZoomSync} isLoading={zoomSync.isSyncing}>
                  {t('connect.zoomSync.syncButton')}
                </Button>
              )}
              <BulkAttendanceButtons
                meetingId={meetingId}
                selectedUserKeys={selectedUserKeys}
                onUpdated={() => setSelectedUserKeys([])}
              />
            </Flex>
          </Flex>
          {!!zoomSync.error && (
            <Alert status="error" mb={6}>
              {getZoomSyncErrorMessage(zoomSync.error, t)}
            </Alert>
          )}
          <DataTable
            id="meeting-learners"
            columns={getMeetingLearnersTableColumns(t, zoomSync.matchedByUserKey)}
            data={students}
            paginationData={meetingUserPaginationData}
            checkboxConfig={checkboxConfig}
            filters={[attendanceStatusFilter]}
            cta={
              <AttendanceSelectionControls
                selectedCount={selectedUserKeys.length}
                allSelected={allFilteredSelected}
                isDisabled={filteredUserKeys.length === 0}
                onToggle={() => onSelectAllFiltered(!allFilteredSelected)}
              />
            }
            isLoading={isLoading || isFetching}
          />
          <Button variant="outline" onClick={handleCsvDownload}>
            Download Student CSV
          </Button>
          {zoomSync.result && <UnmatchedZoomParticipants result={zoomSync.result} />}
        </Box>
      </Box>
    </GridItem>
  );
};

function getMeetingLearnersTableColumns(
  t: TFunction,
  zoomMatches: Record<string, MatchedZoomParticipant>
): DataTableColumn<MeetingUser>[] {
  return [
    {
      id: 'name',
      title: t('connect.meetingUserTableNameHeader'),
      getRow: (learner) => {
        return {
          data: `${learner.user?.firstName} ${learner.user?.lastName} | ${learner.user?.email}`,
          component: <LearnerNameCell learner={learner} />,
        };
      },
    },
    {
      id: 'attendance',
      title: t('connect.attendance'),
      width: '280px',
      getRow: (learner) => {
        return {
          data: learner.attendance?.status || t('connect.notRecorded'),
          component: (
            <AttendanceCell
              attendance={learner.attendance}
              attendanceRequest={learner.attendanceRequest}
              meetingId={learner.meetingId}
              learner={learner}
            />
          ),
        };
      },
    },
    {
      id: 'joinedAt',
      title: t('connect.joinedAt'),
      disableSorting: true,
      getRow: (learner) => {
        return {
          data: learner.joinedAt ? dateFormatter.datetime(learner.joinedAt) : '',
        };
      },
    },
    {
      id: 'joinSource',
      title: t('connect.joinSource'),
      disableSorting: true,
      getRow: (learner) => {
        return {
          data: learner.joinSource ? t(`connect.joinSourceLabel.${learner.joinSource}`) : '',
        };
      },
    },
    {
      id: 'zoomJoinTime',
      title: t('connect.zoomSync.joinTime'),
      getRow: (learner) => {
        const joinTime = zoomMatches[learner.userKey]?.joinTime;
        return {
          data: joinTime ? dateFormatter.datetime(joinTime) : '',
          sortValue: joinTime ? new Date(joinTime).getTime() : 0,
        };
      },
    },
    {
      id: 'zoomDuration',
      title: t('connect.zoomSync.duration'),
      getRow: (learner) => {
        const match = zoomMatches[learner.userKey];
        return {
          data: match ? t('connect.zoomSync.minutes', { minutes: durationInMinutes(match.durationSeconds) }) : '',
          sortValue: match?.durationSeconds ?? 0,
        };
      },
    },
    {
      id: 'lastUpdated',
      title: t('connect.lastUpdatedColumn'),
      width: '200px',
      getRow: (learner) => {
        return {
          data: '',
          sortValue: learner.updatedAt ? new Date(learner.updatedAt).getTime() : 0,
          component: <AttendanceProvenanceBadge source={learner.attendanceSource} editedAt={learner.updatedAt} />,
        };
      },
    },
  ];
}
